import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChatConversation,
    IChatConversationMessage,
    INewConversationData,
    IOpenConversationManageUsersData,
} from '@fi-shared/fi-chat/fi-chat';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { AppTextService } from '@fi-shared/app-text';
import { SafeHtml } from '@angular/platform-browser';
import { MatMenuTrigger } from '@angular/material/menu';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { ManageUsersStep } from '@fi-shared/fi-chat/fi-chat-conversation-manage-type';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { fiTextToHtml, isDefined } from '@fi-shared/utils';
import { RelativeDateService } from '@fi-shared/relative-date/relative-date.service';
import { FiChatSourceMessageType } from '@fi-shared/fi-chat/fi-chat-source-message-type';
import { FiChatConversationHelperService } from '../../fi-chat-conversation/fi-chat-conversation-helper.service';

@Component({
    selector: 'fi-chat-conversation-list-item',
    templateUrl: './fi-chat-conversation-list-item.component.html',
    styleUrls: ['./fi-chat-conversation-list-item.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-list-item' },
    standalone: false,
})
export class FiChatConversationListItemComponent implements OnInit {
    @Input() conversation: IChatConversation;
    @Input() isConversationMenuAvailable: boolean;
    @Output() closeConversation = new EventEmitter<IChatConversation>();
    @Output() createNewConversation = new EventEmitter<INewConversationData>();
    @Output() openManageUsersConversation = new EventEmitter<IOpenConversationManageUsersData>();
    @Output() openExistingConversation = new EventEmitter<IChatConversation>();
    @ViewChild('settingsMenuTrigger') settingsMenuTrigger: MatMenuTrigger;
    private daysDifference: number;

    private isMenuShown: boolean = false;

    protected readonly sourceMessageIcons: { [key in FiChatSourceMessageType]: string } = {
        [FiChatSourceMessageType.Reply]: 'fas fa-reply',
        [FiChatSourceMessageType.Forwarded]: 'fas fa-share',
    };

    constructor(
        private fiChatHelperService: FiChatHelperService,
        private fiChatService: FiChatService,
        private fiChatSyncService: FiChatSyncService,
        private cdr: ChangeDetectorRef,
        private appTextService: AppTextService,
        private fiNotificationHelperService: FiNotificationHelperService,
        private detectDeviceService: DetectDevicesService,
        private relativeDateService: RelativeDateService,
        private fiChatConversationHelperService: FiChatConversationHelperService,
    ) {}

    public ngOnInit(): void {
        this.subscribeOnConversationUpdated();
        this.setDifferenceBetweenDates();
    }

    private subscribeOnConversationUpdated(): void {
        this.fiChatSyncService
            .conversationUpdated$(this.conversation.Id)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                setTimeout(() => {
                    this.cdr.detectChanges();
                });
            });
    }

    protected toggleIsShownMenu(isMenuShown: boolean): void {
        if (!this.isConversationMenuAvailable) {
            return;
        }

        this.isMenuShown = isMenuShown;
    }

    protected isSettingsMenuShown(): boolean {
        return this.detectDeviceService.isMobilePhone() ? false : this.isMenuShown;
    }

    protected onLongPress(): void {
        this.settingsMenuTrigger.openMenu();
    }

    protected onMenuClick($event: MouseEvent): void {
        $event.stopPropagation();
        setTimeout(() => this.toggleIsShownMenu(true));
    }

    protected isPersonalChat(): boolean {
        return this.fiChatHelperService.isPersonalChat(this.conversation);
    }

    protected startNewGroupConversation(): void {
        this.createNewConversation.emit({
            type: FiChatConversationType.Group,
            selectedUserIds: this.conversation.Users.map((u) => u.Id),
            message: undefined,
        });
    }

    protected onOpenExistingConversation($event: MouseEvent): void {
        $event.preventDefault();
        this.openExistingConversation.emit(this.conversation);
    }

    protected leaveGroupConversation(): void {
        this.fiChatHelperService.openLeaveGroupConversationDialog().then(() => this.leaveConversation());
    }

    private leaveConversation(): void {
        this.fiChatService.leaveConversation(this.conversation.Id).then(() => {
            this.closeConversation.emit(this.conversation);
        });
    }

    protected removeConversation(): void {
        this.fiChatHelperService.openRemoveConversationDialog().then(() => {
            this.fiChatService
                .updateConversationIsHidden(this.conversation.Id, true)
                .then(() => this.closeConversation.emit(this.conversation));
        });
    }

    protected changeConversationName(): void {
        this.fiChatHelperService
            .openRenameConversationDialog(this.conversation)
            .then((result) => this.fiChatService.changeConversationName(this.conversation.Id, result));
    }

    protected manageGroupUsers(): void {
        this.openManageUsersConversation.emit({
            conversation: this.conversation,
            currentStep: ManageUsersStep.Remove,
        });
    }

    protected addMember(): void {
        this.openManageUsersConversation.emit({
            conversation: this.conversation,
            currentStep: ManageUsersStep.AddMore,
        });
    }

    protected setDifferenceBetweenDates(): void {
        this.daysDifference = this.fiChatHelperService.getDaysDifference(this.conversation.LatestActivity);
    }

    protected getConversationName(): string {
        return this.conversation.Name;
    }

    protected isSourceMessageContentShown(): boolean {
        return this.isOnlySourceMessage() && !this.isMessageDeleted();
    }

    protected isMessageDeleted(): boolean {
        return this.fiChatConversationHelperService.isMessageDeleted(this.conversation.LatestActivityMessage.Type);
    }

    protected getLatestActivityText(
        message?: IChatConversationMessage,
        skipCurrentUserContent?: boolean,
    ): SafeHtml | string {
        const currentMessage: IChatConversationMessage = message ?? this.conversation?.LatestActivityMessage;

        if (!currentMessage) {
            return '';
        }
        if (this.fiChatHelperService.isSystemMessage(currentMessage.Type)) {
            return this.fiChatHelperService.getLatestSystemMessage(currentMessage);
        }
        if (currentMessage.Content && !currentMessage.Links.length) {
            return this.getLatestActivityContent(currentMessage.Content, skipCurrentUserContent);
        }
        return this.fiChatSyncService.getLatestConversationContent(currentMessage);
    }

    private isOnlySourceMessage(): boolean {
        return (
            isDefined(this.conversation.LatestActivityMessage?.SourceMessage) &&
            !this.conversation.LatestActivityMessage.Content?.length &&
            !this.conversation.LatestActivityMessage.MediaBankFileReferences?.length &&
            !this.conversation.LatestActivityMessage.Links?.length
        );
    }

    protected isLatestMessageByCurrentUser(): boolean {
        return this.fiChatHelperService.isCurrentUser(this.conversation.LatestActivityMessage.CreatedByUserId);
    }

    private getLatestActivityContent(content: string, skipCurrentUser?: boolean): string | SafeHtml {
        if (this.isLatestMessageByCurrentUser()) {
            return this.fiChatHelperService.getMessageContent(
                skipCurrentUser ? content : `${this.appTextService.get('common.you')}: ${content}`,
            );
        }
        if (this.fiChatHelperService.isGroupChat(this.conversation)) {
            const userFirstName = fiTextToHtml(this.conversation.LatestActivityMessage.UserFirstName);
            return this.fiChatHelperService.getMessageContent(`${userFirstName}: ${content}`);
        }

        return this.fiChatHelperService.getMessageContent(content);
    }

    protected isNotVisited(): boolean {
        return this.fiNotificationHelperService.isNotVisited(this.conversation);
    }

    protected getConversationDate(): string {
        return this.relativeDateService.getDateForChatLatestActivity(this.conversation.LatestActivity);
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
